import React, { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Building2, Check, ChevronsUpDown, X, Layers, MapPin, Sparkles } from "lucide-react";

/**
 * Reusable project multi-select with quick-select by Region / Category,
 * select-all / clear, remembered selection. `selected = []` => all projects.
 */
export function ProjectMultiSelect({
  projects = [], regions = [], categories = [], teams = [], special = [], selected = [], onChange,
  remember, onRememberChange, className,
  showRegion = true, showCategory = true, showTeam = false,
}) {
  const [open, setOpen] = useState(false);
  const names = useMemo(() => projects.map((p) => p.name), [projects]);
  const selCount = selected.length;
  const isAll = selCount === 0;

  const toggleOne = (name) => {
    if (selected.includes(name)) onChange(selected.filter((n) => n !== name));
    else onChange([...selected, name]);
  };
  const toggleGroup = (groupNames) => {
    const allIn = groupNames.length > 0 && groupNames.every((n) => selected.includes(n));
    if (allIn) onChange(selected.filter((n) => !groupNames.includes(n)));
    else onChange(Array.from(new Set([...selected, ...groupNames])));
  };
  const groupNamesFor = (key, val) => projects.filter((p) => p[key] === val).map((p) => p.name);

  const specialByName = useMemo(() => Object.fromEntries((special || []).map((s) => [s.name, s])), [special]);
  const label = isAll
    ? "All projects"
    : selCount === 1 && specialByName[selected[0]]
      ? specialByName[selected[0]].label
      : `${selCount} selection${selCount === 1 ? "" : "s"}`;
  const chips = isAll ? [] : selected.slice(0, 2);
  const extra = isAll ? 0 : Math.max(0, selCount - chips.length);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="project-multiselect-trigger"
          className={cn(
            "inline-flex min-w-[190px] items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm transition-colors duration-150 hover:bg-[hsl(var(--surface-2))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]",
            className
          )}
        >
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{label}</span>
          <span className="flex items-center gap-1">
            {chips.map((c) => (
              <span key={c} className="hidden rounded-full border px-2 py-0.5 text-[10px] text-muted-foreground sm:inline">{c}</span>
            ))}
            {extra > 0 && <span className="hidden rounded-full border px-2 py-0.5 text-[10px] text-muted-foreground sm:inline">+{extra}</span>}
          </span>
          <ChevronsUpDown className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[520px] p-0" data-testid="project-multiselect-popover">
        <div className="grid grid-cols-12">
          {/* Left: quick filters + actions */}
          <div className="col-span-5 border-r bg-[hsl(var(--surface-2))] p-3">
            {(special || []).length > 0 && (<>
            <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"><Sparkles className="h-3 w-3" /> Category</p>
            <div className="flex flex-wrap gap-1.5" data-testid="project-multiselect-quick-special">
              {special.map((sp) => {
                const active = selected.includes(sp.name);
                return (
                  <button key={sp.name} type="button" onClick={() => toggleOne(sp.name)} title={sp.desc}
                    className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors duration-150",
                      active ? "border-primary bg-[hsl(var(--primary)/0.12)] text-primary" : "hover:bg-card")}
                    data-testid={`project-quick-special-${sp.label}`}>{sp.label}</button>
                );
              })}
            </div>
            <p className="mt-1.5 text-[10px] leading-snug text-muted-foreground">Office = Signet · Digital Support = Technology (shown separately from Residential / Commercial projects).</p>
            <Separator className="my-3" />
            </>)}
            {showRegion && (<>
            <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"><MapPin className="h-3 w-3" /> By region</p>
            <div className="flex flex-wrap gap-1.5" data-testid="project-multiselect-quick-region">
              {regions.map((r) => {
                const gn = groupNamesFor("region", r);
                const active = gn.length > 0 && gn.every((n) => selected.includes(n));
                return (
                  <button key={r} type="button" onClick={() => toggleGroup(gn)}
                    className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors duration-150",
                      active ? "border-primary bg-[hsl(var(--primary)/0.12)] text-primary" : "hover:bg-card")}
                    data-testid={`project-quick-region-${r}`}>{r}</button>
                );
              })}
            </div>
            </>)}
            {showCategory && (<>
            <p className="mb-1.5 mt-3 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"><Layers className="h-3 w-3" /> By category</p>
            <div className="flex flex-wrap gap-1.5" data-testid="project-multiselect-quick-category">
              {categories.map((c) => {
                const gn = groupNamesFor("category", c);
                const active = gn.length > 0 && gn.every((n) => selected.includes(n));
                return (
                  <button key={c} type="button" onClick={() => toggleGroup(gn)}
                    className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors duration-150",
                      active ? "border-primary bg-[hsl(var(--primary)/0.12)] text-primary" : "hover:bg-card")}
                    data-testid={`project-quick-category-${c}`}>{c}</button>
                );
              })}
            </div>
            </>)}
            {showTeam && teams.length > 0 && (<>
            <p className="mb-1.5 mt-3 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"><Layers className="h-3 w-3" /> By team</p>
            <div className="flex flex-wrap gap-1.5" data-testid="project-multiselect-quick-team">
              {teams.map((t) => {
                const gn = groupNamesFor("team", t);
                const active = gn.length > 0 && gn.every((n) => selected.includes(n));
                return (
                  <button key={t} type="button" onClick={() => toggleGroup(gn)}
                    className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors duration-150",
                      active ? "border-primary bg-[hsl(var(--primary)/0.12)] text-primary" : "hover:bg-card")}
                    data-testid={`project-quick-team-${t}`}>{t}</button>
                );
              })}
            </div>
            </>)}
            <Separator className="my-3" />
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-7 flex-1 text-xs" onClick={() => onChange(names)} data-testid="project-multiselect-select-all">Select all</Button>
              <Button variant="ghost" size="sm" className="h-7 flex-1 text-xs" onClick={() => onChange([])} data-testid="project-multiselect-clear">
                <X className="mr-1 h-3 w-3" /> Clear
              </Button>
            </div>
            {typeof remember === "boolean" && (
              <div className="mt-3 flex items-center justify-between rounded-md border bg-card px-2.5 py-2">
                <span className="text-xs text-muted-foreground">Remember selection</span>
                <Switch checked={remember} onCheckedChange={onRememberChange} data-testid="project-multiselect-remember-switch" />
              </div>
            )}
          </div>
          {/* Right: searchable list */}
          <div className="col-span-7 p-0">
            <Command>
              <CommandInput placeholder="Search projects…" data-testid="project-multiselect-search" />
              <CommandList>
                <CommandEmpty>No projects found.</CommandEmpty>
                <CommandGroup>
                  <ScrollArea className="h-[260px]">
                    {projects.map((p) => {
                      const checked = isAll || selected.includes(p.name);
                      return (
                        <CommandItem key={p.name} value={p.name} onSelect={() => toggleOne(p.name)}
                          className="flex items-center gap-2" data-testid="project-multiselect-option">
                          <Checkbox checked={checked && !isAll} className="pointer-events-none" />
                          <div className="min-w-0 flex-1">
                            <p className="flex items-center gap-1.5 truncate text-sm">{p.name}</p>
                            <p className="truncate text-[10px] text-muted-foreground">{p.region} {"\u00b7"} {p.category}</p>
                          </div>
                          {!isAll && selected.includes(p.name) && <Check className="h-3.5 w-3.5 text-primary" />}
                        </CommandItem>
                      );
                    })}
                  </ScrollArea>
                </CommandGroup>
              </CommandList>
            </Command>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
